import { useState } from 'react'
import DashboardCard from './DashboardCard'
import SearchBar from './SearchBar'
import { DashboardAccessInfo } from '../types/dashboard'
import { useDashboards } from '../hooks/useDashboards'
import './DashboardGrid.css'

// Plausible tracking helper
const trackEvent = (eventName: string, props?: Record<string, any>) => {
  if (typeof window !== 'undefined' && (window as any).plausible) {
    (window as any).plausible(eventName, { props });
  }
}

interface DashboardGridProps {
  className?: string
  showHeader?: boolean
  showStats?: boolean
  showSearch?: boolean
  showFavorites?: boolean
  showRecent?: boolean
  maxRecent?: number
}

const DashboardGrid = ({
  className = '',
  showHeader = true,
  showStats = true,
  showSearch = true,
  showFavorites = true,
  showRecent = true,
  maxRecent = 4
}: DashboardGridProps) => {
  const {
    dashboardsWithAccess,
    favoriteDashboards,
    recentDashboards,
    dashboardStats,
    filterOptions,
    searchSuggestions,
    error,
    navigateToDashboard,
    openDashboardInNewTab,
    toggleFavorite,
    updateFilters,
    isFavorite,
    isRecent
  } = useDashboards()

  const [viewMode, setViewMode] = useState<'all' | 'favorites' | 'recent'>('all')

  const handleDashboardClick = async (dashboardId: string) => {
    const dashboard = dashboardsWithAccess.find(d => d.dashboard.id === dashboardId)?.dashboard;
    trackEvent('Dashboard Accessed', {
      dashboardId,
      dashboardName: dashboard?.name || 'Unknown',
      dashboardCategory: dashboard?.category || 'Unknown',
      viewMode,
      accessMethod: 'click'
    });
    await navigateToDashboard(dashboardId)
  }

  const handleToggleFavorite = (dashboardId: string) => {
    const dashboard = dashboardsWithAccess.find(d => d.dashboard.id === dashboardId)?.dashboard;
    const wasFavorite = isFavorite(dashboardId);
    
    trackEvent('Dashboard Favorite Toggled', {
      dashboardId,
      dashboardName: dashboard?.name || 'Unknown',
      dashboardCategory: dashboard?.category || 'Unknown',
      action: wasFavorite ? 'removed' : 'added',
      viewMode
    });
    
    toggleFavorite(dashboardId)
  }

  const handleOpenInNewTab = async (dashboardId: string) => {
    const dashboard = dashboardsWithAccess.find(d => d.dashboard.id === dashboardId)?.dashboard;
    trackEvent('Dashboard Opened in New Tab', {
      dashboardId,
      dashboardName: dashboard?.name || 'Unknown',
      dashboardCategory: dashboard?.category || 'Unknown',
      viewMode
    });
    await openDashboardInNewTab(dashboardId)
  }

  const getCurrentDashboards = (): DashboardAccessInfo[] => {
    switch (viewMode) {
      case 'favorites':
        return favoriteDashboards.map(dashboard => ({
          dashboard,
          hasAccess: true,
          missingRoles: []
        }))
      case 'recent':
        return recentDashboards.slice(0, maxRecent).map(dashboard => ({
          dashboard,
          hasAccess: true,
          missingRoles: []
        }))
      default:
        return dashboardsWithAccess
    }
  }

  const currentDashboards = getCurrentDashboards()
  const accessibleCount = currentDashboards.length

  // Error state
  if (error) {
    return (
      <div className={`dashboard-grid error ${className}`}>
        <div className="error-container">
          <div className="error-icon">⚠️</div>
          <h3 className="error-title">Failed to Load Dashboards</h3>
          <p className="error-message">{error.message}</p>
          <button 
            className="retry-btn"
            onClick={() => window.location.reload()}
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className={`dashboard-grid ${className}`}>
      {/* Header */}
      {showHeader && (
        <div className="grid-header">
          <div className="header-content">
            <h2 className="grid-title">
              {viewMode === 'all' && 'Your Dashboards'}
              {viewMode === 'favorites' && '⭐ Favorite Dashboards'}
              {viewMode === 'recent' && '⏱️ Recent Dashboards'}
            </h2>
            
            {showStats && viewMode === 'all' && (
              <div className="dashboard-stats">
                <div className="stat-item">
                  <span className="stat-value">{accessibleCount}</span>
                  <span className="stat-label">Available</span>
                </div>
                <div className="stat-item">
                  <span className="stat-value">{dashboardStats.favoriteCount}</span>
                  <span className="stat-label">Favorites</span>
                </div>
                <div className="stat-item">
                  <span className="stat-value">{dashboardStats.recentCount}</span>
                  <span className="stat-label">Recent</span>
                </div>
              </div>
            )}
          </div>

          {/* View Mode Tabs */}
          <div className="view-tabs">
            <button
              className={`view-tab ${viewMode === 'all' ? 'active' : ''}`}
              onClick={() => {
                trackEvent('Dashboard View Changed', {
                  fromView: viewMode,
                  toView: 'all',
                  totalDashboards: dashboardsWithAccess.length
                });
                setViewMode('all');
              }}
            >
              📊 All Dashboards
            </button>
            {showFavorites && favoriteDashboards.length > 0 && (
              <button
                className={`view-tab ${viewMode === 'favorites' ? 'active' : ''}`}
                onClick={() => {
                  trackEvent('Dashboard View Changed', {
                    fromView: viewMode,
                    toView: 'favorites',
                    favoriteCount: favoriteDashboards.length
                  });
                  setViewMode('favorites');
                }}
              >
                ⭐ Favorites ({favoriteDashboards.length})
              </button>
            )}
            {showRecent && recentDashboards.length > 0 && (
              <button
                className={`view-tab ${viewMode === 'recent' ? 'active' : ''}`}
                onClick={() => {
                  trackEvent('Dashboard View Changed', {
                    fromView: viewMode,
                    toView: 'recent',
                    recentCount: recentDashboards.length
                  });
                  setViewMode('recent');
                }}
              >
                ⏱️ Recent ({Math.min(recentDashboards.length, maxRecent)})
              </button>
            )}
          </div>
        </div>
      )}

      {/* Search Bar */}
      {showSearch && viewMode === 'all' && (
        <SearchBar
          onFiltersChange={updateFilters}
          categories={filterOptions.categories}
          tags={filterOptions.tags}
          suggestions={searchSuggestions}
          placeholder="Search dashboards by name, description, or tags..."
        />
      )}

      {/* Dashboard Grid */}
      <div className="grid-content">
        {currentDashboards.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">
              {viewMode === 'all' && '📊'}
              {viewMode === 'favorites' && '⭐'}
              {viewMode === 'recent' && '⏱️'}
            </div>
            <h3 className="empty-title">
              {viewMode === 'all' && 'No dashboards found'}
              {viewMode === 'favorites' && 'No favorite dashboards'}
              {viewMode === 'recent' && 'No recent dashboards'}
            </h3>
            <p className="empty-message">
              {viewMode === 'all' && 'Try adjusting your search filters or contact your administrator for access.'}
              {viewMode === 'favorites' && 'Start adding dashboards to your favorites by clicking the star icon.'}
              {viewMode === 'recent' && 'Your recently accessed dashboards will appear here.'}
            </p>
            {viewMode !== 'all' && (
              <button
                className="view-all-btn"
                onClick={() => setViewMode('all')}
              >
                View All Dashboards
              </button>
            )}
          </div>
        ) : (
          <div className="dashboards-grid">
            {currentDashboards.map((item) => (
              <DashboardCard
                key={item.dashboard.id}
                dashboard={item.dashboard}
                hasAccess={true}
                isFavorite={isFavorite(item.dashboard.id)}
                isRecent={isRecent(item.dashboard.id)}
                onNavigate={handleDashboardClick}
                onToggleFavorite={handleToggleFavorite}
                onOpenInNewTab={handleOpenInNewTab}
              />
            ))}
          </div>
        )}
      </div>

      {/* Quick Access Section */}
      {viewMode === 'all' && (showFavorites || showRecent) && (
        <div className="quick-access">
          {showFavorites && favoriteDashboards.length > 0 && (
            <div className="quick-section">
              <h4 className="quick-title">⭐ Quick Access - Favorites</h4>
              <div className="quick-grid">
                {favoriteDashboards.slice(0, 3).map((dashboard) => (
                  <div
                    key={dashboard.id}
                    className="quick-item"
                    onClick={() => handleDashboardClick(dashboard.id)}
                  >
                    <span className="quick-name">{dashboard.name}</span>
                    <span className="quick-category">{dashboard.category || 'General'}</span>
                  </div>
                ))}
                {favoriteDashboards.length > 3 && (
                  <div 
                    className="quick-item more"
                    onClick={() => setViewMode('favorites')}
                  >
                    <span className="quick-name">+{favoriteDashboards.length - 3} more</span>
                    <span className="quick-category">View all favorites</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {showRecent && recentDashboards.length > 0 && (
            <div className="quick-section">
              <h4 className="quick-title">⏱️ Recently Accessed</h4>
              <div className="quick-grid">
                {recentDashboards.slice(0, 3).map((dashboard) => (
                  <div
                    key={dashboard.id}
                    className="quick-item"
                    onClick={() => handleDashboardClick(dashboard.id)}
                  >
                    <span className="quick-name">{dashboard.name}</span>
                    <span className="quick-category">{dashboard.category || 'General'}</span>
                  </div>
                ))}
                {recentDashboards.length > 3 && (
                  <div 
                    className="quick-item more"
                    onClick={() => setViewMode('recent')}
                  >
                    <span className="quick-name">+{recentDashboards.length - 3} more</span>
                    <span className="quick-category">View all recent</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default DashboardGrid 